/**
 * The rules of a run (UI-002): what a shot costs, what a scratch does, when it's over.
 *
 * ⚠️ This lives in `sim` rather than in `apps/web` for the same reason the layout
 * generator does. The Phase 4 Worker replays a submitted `ShotLog` through this same
 * package and computes the score itself (ARCHITECTURE.md §6.3) — so it has to apply
 * the between-shot rules too. A cue ball respotted by one rule in the browser and a
 * different rule on the server would diverge on the shot after every scratch, and the
 * run would fail verification through no fault of the player.
 *
 * So these ARE physics, in the sense that matters: part of the deterministic model
 * both sides must agree on. The determinism rules apply in full — no clock, no
 * randomness, no transcendentals (ARCHITECTURE.md §5.5).
 *
 * The rules themselves are PRD §4:
 *   - every shot counts, including a complete miss
 *   - potting several balls in one shot is allowed and encouraged
 *   - a scratch costs +1 and puts the cue ball back where it was before that shot,
 *     at the nearest free spot if something has since rolled onto it
 *   - balls legitimately potted on a scratch shot STAY potted, and if the last one
 *     drops on a scratch, the puzzle is complete with the penalty applied
 *   - score = shots taken + penalty strokes
 */
import { DEFAULT_PHYSICS, type PhysicsConfig } from './config.ts';
import { DEFAULT_CONSTRAINTS, type LayoutConstraints } from './layout.ts';
import { simulateShot } from './simulate.ts';
import { stateFromSnapshots } from './state.ts';
import type { Point, TableGeometry } from './table.ts';
import {
  CUE_BALL,
  type BallId,
  type BallSnapshot,
  type Layout,
  type ShotInput,
  type SimResult,
} from './types.ts';

/** A run in progress. Immutable: every shot produces a new one. */
export interface RunState {
  readonly balls: readonly BallSnapshot[];
  readonly shots: number;
  readonly penalties: number;
  readonly complete: boolean;
}

/** What one shot did — enough to drive the HUD, the share card and the shot log. */
export interface ShotOutcome {
  /** Object balls potted by THIS shot, ascending. Never includes the cue ball. */
  readonly pottedIds: readonly BallId[];
  readonly scratched: boolean;
  /** Object balls still on the table after the shot. */
  readonly ballsRemaining: number;
  readonly complete: boolean;
  /** Where the cue ball was put back, when it scratched. */
  readonly respot: Point | undefined;
}

export interface ShotResolution {
  readonly run: RunState;
  readonly outcome: ShotOutcome;
}

/** Score = shots taken + penalty strokes (PRD §4). Compared against par in Phase 2. */
export function runScore(run: RunState): number {
  return run.shots + run.penalties;
}

/** A fresh run on a freshly dealt table. */
export function startRun(layout: Layout): RunState {
  const balls: BallSnapshot[] = [atRest(CUE_BALL, layout.cue.xM, layout.cue.yM)];
  for (let i = 0; i < layout.balls.length; i++) {
    const ball = layout.balls[i];
    if (ball === undefined) continue;
    balls.push(atRest(i + 1, ball.xM, ball.yM));
  }
  return { balls, shots: 0, penalties: 0, complete: false };
}

/**
 * Apply a simulated shot to a run.
 *
 * `result.finalState` is the table as the physics left it. Everything here is the
 * rules layer on top of that: counting the shot, spotting the cue ball if it went
 * down, and deciding whether the puzzle is over.
 */
export function resolveShot(
  run: RunState,
  result: SimResult,
  geometry: TableGeometry,
  config: PhysicsConfig = DEFAULT_PHYSICS,
  constraints: LayoutConstraints = DEFAULT_CONSTRAINTS,
): ShotResolution {
  const wasPotted = new Set<BallId>();
  for (const ball of run.balls) if (ball.potted) wasPotted.add(ball.id);

  const pottedIds: BallId[] = [];
  let scratched = false;
  for (const ball of result.finalState) {
    if (!ball.potted || wasPotted.has(ball.id)) continue;
    if (ball.id === CUE_BALL) scratched = true;
    else pottedIds.push(ball.id);
  }
  pottedIds.sort((a, b) => a - b);

  const cueBefore = run.balls.find((ball) => ball.id === CUE_BALL);
  const respot =
    scratched && cueBefore !== undefined
      ? findRespot(cueBefore, result.finalState, geometry, config, constraints)
      : undefined;

  const balls = result.finalState.map((ball) =>
    // Back on the table, at rest, with no memory of the spin that put it down.
    ball.id === CUE_BALL && respot !== undefined ? atRest(CUE_BALL, respot.xM, respot.yM) : ball,
  );

  let ballsRemaining = 0;
  for (const ball of balls) if (ball.id !== CUE_BALL && !ball.potted) ballsRemaining++;

  /*
   * The last ball dropping on a scratch still finishes the puzzle — the penalty is
   * applied and the run is over (PRD §4). This is why `complete` is decided from the
   * object balls alone and never from whether the cue ball survived.
   */
  const complete = ballsRemaining === 0;

  return {
    run: {
      balls,
      shots: run.shots + 1,
      penalties: run.penalties + (scratched ? 1 : 0),
      complete,
    },
    outcome: { pottedIds, scratched, ballsRemaining, complete, respot },
  };
}

/**
 * Where the cue ball goes back after a scratch.
 *
 * Its own spot if nothing has rolled onto it, which is the overwhelmingly common case
 * and is the rule as written. Otherwise the nearest spot that is clear of every ball,
 * fully on the table, and not sitting in a pocket.
 *
 * The search walks outward in square rings a ball-radius apart and keeps the closest
 * candidate by true distance — a ring is scanned in a fixed order and rings stop once
 * none of them can beat what has been found, so the answer depends on nothing but the
 * positions. That matters more than elegance here: the Worker must land on the same
 * spot from the same inputs, years later, or the replay diverges.
 */
function findRespot(
  original: BallSnapshot,
  after: readonly BallSnapshot[],
  geometry: TableGeometry,
  config: PhysicsConfig,
  constraints: LayoutConstraints,
): Point {
  const R = config.ballRadiusM;
  const obstacles = after.filter((ball) => ball.id !== CUE_BALL && !ball.potted);

  const minSeparation = constraints.minSeparationR * R;
  const minSeparationSquared = minSeparation * minSeparation;

  const clearOfBalls = (xM: number, yM: number): boolean => {
    for (const ball of obstacles) {
      const dx = ball.xM - xM;
      const dy = ball.yM - yM;
      if (dx * dx + dy * dy < minSeparationSquared) return false;
    }
    return true;
  };

  // The rule is "returns to where it was"; only something standing there overrides it.
  if (clearOfBalls(original.xM, original.yM)) {
    return { xM: original.xM, yM: original.yM };
  }

  const { widthM, heightM } = geometry.spec;
  const usable = (xM: number, yM: number): boolean => {
    // A ball's centre cannot be nearer than R to a cushion: it would be inside it.
    if (xM < R || xM > widthM - R || yM < R || yM > heightM - R) return false;
    for (const pocket of geometry.pockets) {
      const dx = pocket.centre.xM - xM;
      const dy = pocket.centre.yM - yM;
      const clearance = pocket.throatRadiusM + R;
      if (dx * dx + dy * dy < clearance * clearance) return false;
    }
    return clearOfBalls(xM, yM);
  };

  const step = R;
  const maxRing = Math.ceil((widthM + heightM) / step);
  let best: Point | undefined;
  let bestDistanceSquared = Number.POSITIVE_INFINITY;

  for (let ring = 1; ring <= maxRing; ring++) {
    // The closest any point in this ring can be is `ring * step`, on an axis. Once
    // that is no better than what we have, no later ring can improve on it either.
    const ringReach = ring * step;
    if (best !== undefined && ringReach * ringReach >= bestDistanceSquared) break;

    for (let i = -ring; i <= ring; i++) {
      for (let j = -ring; j <= ring; j++) {
        if (i !== -ring && i !== ring && j !== -ring && j !== ring) continue;

        const xM = original.xM + i * step;
        const yM = original.yM + j * step;
        if (!usable(xM, yM)) continue;

        const distanceSquared = i * step * (i * step) + j * step * (j * step);
        if (distanceSquared < bestDistanceSquared) {
          bestDistanceSquared = distanceSquared;
          best = { xM, yM };
        }
      }
    }
  }

  /*
   * Unreachable with 11 balls on a 9ft table — there is always somewhere to put it —
   * but "unreachable" is not a reason to return a silently wrong position. If this
   * ever throws, the caller has handed us a table that is not a table.
   */
  if (best === undefined) {
    throw new Error('resolveShot: no free spot on the table to respot the cue ball');
  }
  return best;
}

function atRest(id: BallId, xM: number, yM: number): BallSnapshot {
  return { id, xM, yM, vxMps: 0, vyMps: 0, wxRadS: 0, wyRadS: 0, wzRadS: 0, potted: false };
}

/**
 * Replay a whole run from its shot log — the headless form of playing it.
 *
 * This is what the Phase 4 Worker does with a submission (ARCHITECTURE.md §6.3): take
 * the layout the level was generated from, apply the player's shots in order through
 * the same physics and the same rules, and look at what is left. The client's claimed
 * score is never read, because it is never sent; the final table is the evidence.
 *
 * Here in Phase 1 it is how SEC-001 proves the log is sufficient: replaying a recorded
 * run has to land on the identical table, to the bit, or the log is not the record it
 * claims to be.
 */
export function replayShots(
  layout: Layout,
  shots: readonly ShotInput[],
  geometry: TableGeometry,
  config: PhysicsConfig = DEFAULT_PHYSICS,
  constraints: LayoutConstraints = DEFAULT_CONSTRAINTS,
): RunState {
  let run = startRun(layout);
  for (const shot of shots) {
    const result = simulateShot(stateFromSnapshots(run.balls), shot, config, geometry);
    run = resolveShot(run, result, geometry, config, constraints).run;
  }
  return run;
}
